import React, { useRef, useState } from "react";
import axios from "axios";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import CheckCircleOutlineRoundedIcon from "@mui/icons-material/CheckCircleOutlineRounded";
import LockResetRoundedIcon from "@mui/icons-material/LockResetRounded";
import { Link, useHistory } from "react-router-dom";
import { farmageo_api } from "../../../config";
import "./passwordRecoveryOtp.scss";

const EMPTY_CODE = ["", "", "", "", "", ""];

const PasswordRecoveryOtp = () => {
  const history = useHistory();
  const inputRefs = useRef([]);
  const validationInFlight = useRef(false);
  const [digits, setDigits] = useState(EMPTY_CODE);
  const [codigoValidado, setCodigoValidado] = useState(false);
  const [codeLeaving, setCodeLeaving] = useState(false);
  const [password, setPassword] = useState("");
  const [repetir, setRepetir] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const codigo = digits.join("");

  const focusInput = (index) => {
    const input = inputRefs.current[index];
    if (input) input.focus();
  };

  const validateCodeValue = async (codeToValidate) => {
    if (validationInFlight.current || !/^\d{6}$/.test(codeToValidate)) return;
    validationInFlight.current = true;
    setError("");
    try {
      setLoading(true);
      await axios.post(`${farmageo_api}/password-recovery/otp/validate`, {
        codigo: codeToValidate,
      });
      setCodeLeaving(true);
      setTimeout(() => {
        setCodigoValidado(true);
        setCodeLeaving(false);
      }, 520);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "El código no es válido o ya venció. Solicitá uno nuevo.",
      );
    } finally {
      validationInFlight.current = false;
      setLoading(false);
    }
  };

  const validateWhenComplete = (nextDigits) => {
    if (nextDigits.every((digit) => digit !== "")) {
      setTimeout(() => validateCodeValue(nextDigits.join("")), 0);
    }
  };

  const setCodeFrom = (value, startIndex = 0) => {
    const incoming = String(value).replace(/\D/g, "").slice(0, 6 - startIndex);
    if (!incoming) return;
    const next = [...digits];
    incoming.split("").forEach((digit, offset) => {
      next[startIndex + offset] = digit;
    });
    setDigits(next);
    focusInput(Math.min(startIndex + incoming.length, 5));
    validateWhenComplete(next);
  };

  const handleDigitChange = (index, value) => {
    const numeric = value.replace(/\D/g, "");
    if (numeric.length > 1) return setCodeFrom(numeric, index);
    const next = [...digits];
    next[index] = numeric;
    setDigits(next);
    if (numeric && index < 5) focusInput(index + 1);
    validateWhenComplete(next);
  };

  const handleKeyDown = (index, event) => {
    if (event.key === "Backspace" && !digits[index] && index > 0) {
      focusInput(index - 1);
    }
    if (event.key === "ArrowLeft" && index > 0) {
      focusInput(index - 1);
    }
    if (event.key === "ArrowRight" && index < 5) {
      focusInput(index + 1);
    }
  };

  const validateCode = async (event) => {
    event.preventDefault();
    if (!/^\d{6}$/.test(codigo)) {
      return setError("Completá los 6 dígitos del código.");
    }
    await validateCodeValue(codigo);
  };

  const submitPassword = async (event) => {
    event.preventDefault();
    setError("");
    if (password.length < 8) {
      return setError("La contraseña debe tener al menos 8 caracteres.");
    }
    if (password !== repetir) {
      return setError("Las contraseñas no coinciden.");
    }
    try {
      setLoading(true);
      await axios.post(`${farmageo_api}/password-recovery/otp`, {
        codigo,
        password,
      });
      setSuccess(true);
    } catch (requestError) {
      setError(
        requestError.response?.data?.message ||
          "No pudimos actualizar la contraseña. Intentá nuevamente.",
      );
    } finally {
      setLoading(false);
    }
  };

  const editCode = () => {
    setCodigoValidado(false);
    setCodeLeaving(false);
    setPassword("");
    setRepetir("");
    setError("");
    setTimeout(() => focusInput(0), 0);
  };

  return (
    <main className="otp-recovery">
      <section className="otp-recovery__card">
        {success ? (
          <div className="otp-recovery__success">
            <CheckCircleOutlineRoundedIcon />
            <h1>Contraseña actualizada</h1>
            <p>Ya podés ingresar con tu nueva contraseña.</p>
            <Button variant="contained" onClick={() => history.replace("/login")}>
              Ir al inicio de sesión
            </Button>
          </div>
        ) : (
          <>
            <div className="otp-recovery__icon">
              {codigoValidado ? (
                <CheckCircleOutlineRoundedIcon />
              ) : (
                <LockResetRoundedIcon />
              )}
            </div>
            <h1>{codigoValidado ? "Creá tu contraseña" : "Ingresá tu código"}</h1>
            <p className="otp-recovery__intro">
              {codigoValidado
                ? "Código validado. Ahora elegí una contraseña nueva."
                : "Te enviamos un código de 6 dígitos por WhatsApp."}
            </p>

            <form onSubmit={codigoValidado ? submitPassword : validateCode} noValidate>
              {!codigoValidado ? (
                <div
                  className={`otp-recovery__code-stage${codeLeaving ? " is-leaving" : ""}`}
                >
                  <div className="otp-recovery__digits" aria-label="Código de verificación">
                    {digits.map((digit, index) => (
                      <input
                        key={index}
                        ref={(element) => {
                          inputRefs.current[index] = element;
                        }}
                        autoFocus={index === 0}
                        value={digit}
                        disabled={loading || codeLeaving}
                        inputMode="numeric"
                        autoComplete={index === 0 ? "one-time-code" : "off"}
                        maxLength={1}
                        aria-label={`Dígito ${index + 1}`}
                        onChange={(event) => handleDigitChange(index, event.target.value)}
                        onKeyDown={(event) => handleKeyDown(index, event)}
                        onPaste={(event) => {
                          event.preventDefault();
                          setCodeFrom(event.clipboardData.getData("text"), index);
                        }}
                      />
                    ))}
                  </div>
                  <div className="otp-recovery__auto-status">
                    {loading ? "Validando código…" : "Se valida automáticamente al completarlo"}
                  </div>
                  {error ? <div className="otp-recovery__error">{error}</div> : null}
                </div>
              ) : (
                <div className="otp-recovery__password-fields">
                  <button className="otp-recovery__edit-code" type="button" onClick={editCode}>
                    Cambiar código
                  </button>
                  <TextField
                    autoFocus
                    fullWidth
                    label="Nueva contraseña"
                    type="password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                  <TextField
                    fullWidth
                    label="Repetir contraseña"
                    type="password"
                    value={repetir}
                    onChange={(event) => setRepetir(event.target.value)}
                    inputProps={{ autoComplete: "new-password" }}
                  />
                  {error ? <div className="otp-recovery__error">{error}</div> : null}
                  <Button fullWidth type="submit" variant="contained" size="large" disabled={loading}>
                    {loading ? "Guardando…" : "Cambiar contraseña"}
                  </Button>
                </div>
              )}
            </form>

            <Link className="otp-recovery__back" to="/login">
              Volver al inicio de sesión
            </Link>
          </>
        )}
      </section>
    </main>
  );
};

export default PasswordRecoveryOtp;
